/**
 * Shared request/response contract for the Inventory V2 read API, mounted at
 * /accounts/:accountId/inventory. The :accountId path param is the authz
 * context; the required `viewScope` body field is the lens being queried,
 * resolved and entitlement-checked server-side.
 *
 * Rows are at (location, product) grain. Row-level brand fields (`brandId`,
 * `brandName`) mean POS Brand in EVERY scope; vendor scopes constrain which
 * rows appear (via BrandLink), they don't reinterpret the fields.
 */
import { z } from 'zod';
import { AccountMarket, AccountMarkets } from '../account';
import { objectIdRegex } from '../util/regex';

/**
 * Stock-status wire values, named after the UI status legend. The StockStatus
 * type, request validation, and STOCK_STATUSES order all derive from this
 * record.
 */
export const StockStatuses = {
  OutOfStock: 'out-of-stock',
  LowStock: 'low-stock',
  InStock: 'in-stock',
  Overstock: 'overstock',
  Inactive: 'inactive',
  Stale: 'stale',
} as const;

export type StockStatus = typeof StockStatuses[keyof typeof StockStatuses];

/** Every stock status, in UI legend order. */
export const STOCK_STATUSES = Object.values(StockStatuses);

/** The four "live" statuses surfaced by default in inventory views. */
export const PRIMARY_STOCK_STATUSES = [
  StockStatuses.OutOfStock,
  StockStatuses.LowStock,
  StockStatuses.InStock,
  StockStatuses.Overstock,
] as const;

export type PrimaryStockStatus = typeof PRIMARY_STOCK_STATUSES[number];

/** "Old products" per the tech spec: stock that is no longer moving. */
export const OLD_STOCK_STATUSES = [StockStatuses.Stale, StockStatuses.Inactive] as const;

export type OldStockStatus = typeof OLD_STOCK_STATUSES[number];

export const isPrimaryStockStatus = (status: StockStatus): status is PrimaryStockStatus =>
  (PRIMARY_STOCK_STATUSES as readonly StockStatus[]).includes(status);

export const isOldStockStatus = (status: StockStatus): status is OldStockStatus =>
  (OLD_STOCK_STATUSES as readonly StockStatus[]).includes(status);

export const sortDirectionSchema = z.enum(['asc', 'desc']);

export type SortDirection = z.infer<typeof sortDirectionSchema>;

/**
 * Filters are scope-uniform: every scope accepts the same fields.
 *
 * Per-scope vocabulary for `brandIds` (ids round-trip from the same scope's
 * filter-options, so clients never translate):
 * - retailer scope: POS Brand ids, filtered in SQL against `brand_id`.
 * - vendor scopes: the vendor's SparkPlug Brand ids, absorbed during scope
 *   resolution to narrow which BrandLinks grant POS brands.
 *
 * Search is a case-insensitive partial match on product name.
 */
export const inventoryFiltersSchema = z.object({
  locationIds: z.array(z.string()).optional(),
  brandIds: z.array(z.string()).optional(),
  productCategories: z.array(z.string()).optional(),
  productSubcategories: z.array(z.string()).optional(),
  stockStatuses: z.array(z.nativeEnum(StockStatuses)).optional(),
  search: z.string().max(200).optional(),
});

export type InventoryFilters = z.infer<typeof inventoryFiltersSchema>;

const MAX_LIMIT = 100;

/** Out-of-range pagination is rejected with a 400, not silently clamped. */
const paginationFields = {
  limit: z.number().int().min(1).max(MAX_LIMIT).optional(),
  offset: z.number().int().min(0).optional(),
};

const accountIdSchema = z.string().regex(objectIdRegex, 'must be a Mongo ObjectId');

export const inventoryAccountPathParamsSchema = z.object({
  accountId: accountIdSchema,
});

export type InventoryAccountPathParams = z.infer<typeof inventoryAccountPathParamsSchema>;

export const accountMarketSchema = z.enum(AccountMarkets as [AccountMarket, ...AccountMarket[]]);

/** A retailer viewing its own inventory; retailerId must match :accountId. */
export const inventoryRetailerViewScopeSchema = z.object({
  scope: z.literal('retailer'),
  retailerId: accountIdSchema,
});

/**
 * A vendor viewing its linked retailers across one market; vendorId must
 * match :accountId. Sees only BrandLink-granted rows.
 */
export const inventoryVendorMarketViewScopeSchema = z.object({
  scope: z.literal('vendor-market'),
  vendorId: accountIdSchema,
  market: accountMarketSchema,
});

/**
 * A vendor viewing one linked retailer (all locations); vendorId must match
 * :accountId. Sees only BrandLink-granted rows.
 */
export const inventoryVendorRetailerViewScopeSchema = z.object({
  scope: z.literal('vendor-retailer'),
  vendorId: accountIdSchema,
  retailerId: accountIdSchema,
});

/**
 * The lens being queried, independent of the :accountId authz context.
 * Spark-view scopes join this union later.
 */
export const inventoryViewScopeSchema = z.discriminatedUnion('scope', [
  inventoryRetailerViewScopeSchema,
  inventoryVendorMarketViewScopeSchema,
  inventoryVendorRetailerViewScopeSchema,
]);

export type InventoryViewScope = z.infer<typeof inventoryViewScopeSchema>;
export type InventoryVendorMarketViewScope = z.infer<typeof inventoryVendorMarketViewScopeSchema>;

export interface Paginated<T> {
  rows: T[];
  total: number;
  totalPages: number;
  limit: number;
  offset: number;
}

// --- By Product table ---

export const BY_PRODUCT_SORT_KEYS = [
  'productName',
  'onHandInventory',
  'stockStatus',
  'daysOnHand',
  'velocityPerDay',
  'lastSoldAt',
  'brandName',
  'productCategory',
  'locationName',
] as const;

export type ByProductSortKey = typeof BY_PRODUCT_SORT_KEYS[number];

export interface InventoryByProductRow {
  retailerId: string;
  retailerName: string | null;
  locationId: string;
  locationName: string | null;
  market: string | null;
  productId: string;
  internalKey: string | null;
  productName: string | null;
  productCategory: string | null;
  productSubcategory: string | null;
  brandId: string | null;
  brandName: string | null;
  onHandInventory: number | null;
  /** Mapped server-side from the persisted value; unrecognized values become null. */
  stockStatus: StockStatus | null;
  daysOnHand: number | null;
  /** Daily velocity: units sold over the last 180 days / 180. */
  velocityPerDay: number | null;
  weeklyVelocity: number | null;
  l180Velocity: number | null;
  lastSoldAt: string | null;
  lastInventoryUpdate: string | null;
}

export const inventoryByProductBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  sort: z
    .object({
      sortBy: z.enum(BY_PRODUCT_SORT_KEYS),
      sortDirection: sortDirectionSchema.optional(),
    })
    .optional(),
  ...paginationFields,
});

export type InventoryByProductRequestBody = z.infer<typeof inventoryByProductBodySchema>;

export type InventoryByProductResponse = Paginated<InventoryByProductRow>;

// --- By Location rollup (shared by the table and the chart) ---

export const BY_LOCATION_SORT_KEYS = ['locationName', 'totalUnits', 'totalSkus'] as const;

export type ByLocationSortKey = typeof BY_LOCATION_SORT_KEYS[number];

export type ByLocationMetricSortKey = Extract<ByLocationSortKey, 'totalUnits' | 'totalSkus'>;

export const BY_LOCATION_METRIC_SORT_KEYS = ['totalUnits', 'totalSkus'] as const;

export interface InventoryByLocationRow {
  retailerId: string;
  retailerName: string | null;
  locationId: string;
  locationName: string | null;
  market: string | null;
  totalUnits: number;
  totalSkus: number;
  /** Per-status SKU counts; every StockStatus is present, zero-filled. */
  statusCounts: Record<StockStatus, number>;
}

/** Grand totals across ALL filtered rollup groups (not just the current page). */
export interface InventoryRollupSummary {
  totalUnits: number;
  totalSkus: number;
}

export interface InventoryByLocationResponse extends Paginated<InventoryByLocationRow> {
  summary: InventoryRollupSummary;
}

export const inventoryByLocationBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  sort: z
    .object({
      sortBy: z.enum(BY_LOCATION_SORT_KEYS),
      sortDirection: sortDirectionSchema.optional(),
    })
    .optional(),
  ...paginationFields,
});

export type InventoryByLocationRequestBody = z.infer<typeof inventoryByLocationBodySchema>;

export const inventoryByLocationChartBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  /** Metric to order by; the chart always sorts descending. Defaults to totalUnits. */
  sortBy: z.enum(BY_LOCATION_METRIC_SORT_KEYS).optional(),
  ...paginationFields,
});

export type InventoryByLocationChartRequestBody = z.infer<
  typeof inventoryByLocationChartBodySchema
>;

// --- By Retailer rollup (vendor market scope only; shared by table and chart) ---

export const BY_RETAILER_SORT_KEYS = ['retailerName', 'totalUnits', 'totalSkus'] as const;

export type ByRetailerSortKey = typeof BY_RETAILER_SORT_KEYS[number];

export const BY_RETAILER_METRIC_SORT_KEYS = ['totalUnits', 'totalSkus'] as const;

export type ByRetailerMetricSortKey = typeof BY_RETAILER_METRIC_SORT_KEYS[number];

export interface InventoryByRetailerRow {
  retailerId: string;
  retailerName: string | null;
  /** Locations contributing rows after filters, not the retailer's full roster. */
  locationCount: number;
  totalUnits: number;
  totalSkus: number;
  /** Per-status SKU counts; every StockStatus is present, zero-filled. */
  statusCounts: Record<StockStatus, number>;
}

export interface InventoryByRetailerResponse extends Paginated<InventoryByRetailerRow> {
  summary: InventoryRollupSummary;
}

/**
 * The By Retailer perspective exists only at the vendor market scope, so the
 * body schemas pin `viewScope` to that variant — other scopes fail request
 * validation (400) rather than needing route-level rejection.
 */
export const inventoryByRetailerBodySchema = z.object({
  viewScope: inventoryVendorMarketViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  sort: z
    .object({
      sortBy: z.enum(BY_RETAILER_SORT_KEYS),
      sortDirection: sortDirectionSchema.optional(),
    })
    .optional(),
  ...paginationFields,
});

export type InventoryByRetailerRequestBody = z.infer<typeof inventoryByRetailerBodySchema>;

export const inventoryByRetailerChartBodySchema = z.object({
  viewScope: inventoryVendorMarketViewScopeSchema,
  filters: inventoryFiltersSchema.optional(),
  /** Metric to order by; the chart always sorts descending. Defaults to totalUnits. */
  sortBy: z.enum(BY_RETAILER_METRIC_SORT_KEYS).optional(),
  ...paginationFields,
});

export type InventoryByRetailerChartRequestBody = z.infer<
  typeof inventoryByRetailerChartBodySchema
>;

// --- Filter options ---

export interface InventoryFilterOption {
  id: string;
  name: string;
}

export interface InventoryRetailerFilterOption extends InventoryFilterOption {
  locations: InventoryFilterOption[];
}

export interface InventoryFilterOptionsResponse {
  locations: InventoryFilterOption[];
  /**
   * The Brand dropdown options, in the scope's brand vocabulary: POS Brands
   * for the retailer scope, the vendor's SparkPlug Brands for vendor scopes.
   */
  brands: InventoryFilterOption[];
  productCategories: string[];
  productSubcategories: string[];
  /**
   * vendor-market scope only: the Retailer → Location tree feeding the hybrid
   * "Retailer Location" filter. Checking a retailer client-side expands to its
   * location ids from this tree; the wire filter stays `locationIds`. Derived
   * from the scoped inventory set, so every location that can contribute rows
   * is present.
   */
  retailers?: InventoryRetailerFilterOption[];
}

export const inventoryFilterOptionsBodySchema = z.object({
  viewScope: inventoryViewScopeSchema,
});

export type InventoryFilterOptionsRequestBody = z.infer<typeof inventoryFilterOptionsBodySchema>;
